import React from 'react';
import {
  Table,
  TableColumn,
  EmptyState,
  StatusOK,
  StatusError,
} from '@backstage/core-components';
import { useApi, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Button,
  Chip,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  Tooltip,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

/* ─── Types ─── */

interface TraceResult {
  traceID: string;
  rootServiceName: string;
  rootTraceName: string;
  durationMs: number;
  startTimeUnixNano: string;
  spanSet?: { spans: SpanInfo[] };
  spanSets?: { spans: SpanInfo[] }[];
}

interface SpanInfo {
  spanID: string;
  startTimeUnixNano: string;
  durationNanos: string;
  attributes?: { key: string; value: { stringValue?: string; intValue?: string } }[];
}

interface OTLPSpan {
  traceId: string;
  spanId: string;
  parentSpanId?: string;
  name: string;
  startTimeUnixNano: string;
  endTimeUnixNano: string;
  status?: { code?: number; message?: string };
  attributes?: { key: string; value: { stringValue?: string; intValue?: string } }[];
}

interface OTLPResource {
  attributes?: { key: string; value: { stringValue?: string } }[];
}

interface SpanNode {
  span: OTLPSpan;
  serviceName: string;
  children: SpanNode[];
  depth: number;
  durationMs: number;
  startOffsetMs: number;
}

/* ─── Styles ─── */

const useStyles = makeStyles(theme => ({
  filterBar: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  scatterContainer: {
    marginBottom: theme.spacing(2),
  },
  spanRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.85rem',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  spanLabel: {
    flexShrink: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  spanBar: {
    height: 8,
    borderRadius: 2,
    minWidth: 2,
  },
  spanBarContainer: {
    flex: 1,
    position: 'relative' as const,
    height: 20,
    display: 'flex',
    alignItems: 'center',
  },
  traceDetailHeader: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  serviceChip: {
    margin: '0 2px',
    height: 20,
    fontSize: '0.7rem',
  },
  errorChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    margin: '0 2px',
    height: 20,
    fontSize: '0.7rem',
  },
}));

/* ─── Constants ─── */

const LOOKBACK_OPTIONS = [
  { value: '15m', label: '15 minutes' },
  { value: '30m', label: '30 minutes' },
  { value: '1h', label: '1 hour' },
  { value: '3h', label: '3 hours' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '24 hours' },
];

const LIMIT_OPTIONS = [10, 20, 50, 100];

const SERVICE_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
  '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
];

/* ─── Utilities ─── */

function parseLookback(lookback: string): number {
  const match = lookback.match(/^(\d+)([mh])$/);
  if (!match) return 3600;
  const [, num, unit] = match;
  return parseInt(num, 10) * (unit === 'h' ? 3600 : 60);
}

function formatDuration(ms: number): string {
  if (ms < 0.001) return '<1µs';
  if (ms < 1) return `${(ms * 1000).toFixed(0)}µs`;
  if (ms < 1000) return `${ms.toFixed(1)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatTime(nanos: string): string {
  const ms = parseInt(nanos, 10) / 1e6;
  return new Date(ms).toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getServiceColor(serviceName: string, colorMap: Map<string, string>): string {
  if (!colorMap.has(serviceName)) {
    colorMap.set(serviceName, SERVICE_COLORS[colorMap.size % SERVICE_COLORS.length]);
  }
  return colorMap.get(serviceName)!;
}

/* ─── Trace Detail (Phase 2: Waterfall) ─── */

function buildSpanTree(
  spans: OTLPSpan[],
  resources: Map<string, string>,
  traceStartNano: bigint,
): SpanNode[] {
  const nodeMap = new Map<string, SpanNode>();
  const roots: SpanNode[] = [];

  for (const span of spans) {
    const startNano = BigInt(span.startTimeUnixNano);
    const endNano = BigInt(span.endTimeUnixNano);
    const durationMs = Number(endNano - startNano) / 1e6;
    const startOffsetMs = Number(startNano - traceStartNano) / 1e6;
    const node: SpanNode = {
      span,
      serviceName: resources.get(span.spanId) || 'unknown',
      children: [],
      depth: 0,
      durationMs,
      startOffsetMs,
    };
    nodeMap.set(span.spanId, node);
  }

  for (const span of spans) {
    const node = nodeMap.get(span.spanId)!;
    if (span.parentSpanId && nodeMap.has(span.parentSpanId)) {
      const parent = nodeMap.get(span.parentSpanId)!;
      parent.children.push(node);
      node.depth = parent.depth + 1;
    } else {
      roots.push(node);
    }
  }

  return roots;
}

function flattenTree(nodes: SpanNode[]): SpanNode[] {
  const result: SpanNode[] = [];
  function walk(n: SpanNode) {
    result.push(n);
    n.children.sort((a, b) => a.startOffsetMs - b.startOffsetMs);
    n.children.forEach(walk);
  }
  nodes.sort((a, b) => a.startOffsetMs - b.startOffsetMs);
  nodes.forEach(walk);
  return result;
}

const TraceDetail: React.FC<{
  traceID: string;
  fetchApi: { fetch: typeof fetch };
  authFetch?: (url: string) => Promise<Response | undefined>;
}> = ({ traceID, fetchApi, authFetch }) => {
  const classes = useStyles();
  const [spans, setSpans] = React.useState<SpanNode[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [traceInfo, setTraceInfo] = React.useState({
    duration: 0,
    services: 0,
    totalSpans: 0,
    maxDepth: 0,
  });
  const colorMap = React.useRef(new Map<string, string>());

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const fetchFn = authFetch || fetchApi.fetch.bind(fetchApi);
        const resp = await fetchFn(`/api/proxy/tempo/api/traces/${traceID}`);
        if (!resp) throw new Error('Session expired');
        if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`);
        const data = await resp.json();

        const allSpans: OTLPSpan[] = [];
        const resourceMap = new Map<string, string>();
        const serviceSet = new Set<string>();

        for (const batch of data.batches || []) {
          const res: OTLPResource = batch.resource || {};
          const svcAttr = (res.attributes || []).find(
            (a: { key: string }) => a.key === 'service.name',
          );
          const svcName = svcAttr?.value?.stringValue || 'unknown';
          serviceSet.add(svcName);

          for (const scopeSpan of batch.scopeSpans || []) {
            for (const span of scopeSpan.spans || []) {
              allSpans.push(span);
              resourceMap.set(span.spanId, svcName);
            }
          }
        }

        if (allSpans.length === 0) {
          setSpans([]);
          setLoading(false);
          return;
        }

        const traceStartNano = allSpans.reduce(
          (min, s) => {
            const v = BigInt(s.startTimeUnixNano);
            return v < min ? v : min;
          },
          BigInt(allSpans[0].startTimeUnixNano),
        );
        const traceEndNano = allSpans.reduce(
          (max, s) => {
            const v = BigInt(s.endTimeUnixNano);
            return v > max ? v : max;
          },
          BigInt(allSpans[0].endTimeUnixNano),
        );

        const roots = buildSpanTree(allSpans, resourceMap, traceStartNano);
        const flat = flattenTree(roots);
        const totalDurationMs = Number(traceEndNano - traceStartNano) / 1e6;
        const maxDepth = flat.reduce((m, n) => Math.max(m, n.depth), 0);

        if (!cancelled) {
          setSpans(flat);
          setTraceInfo({
            duration: totalDurationMs,
            services: serviceSet.size,
            totalSpans: allSpans.length,
            maxDepth,
          });
        }
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [traceID, fetchApi]);

  if (loading) return <Typography>Loading trace {traceID.substring(0, 16)}...</Typography>;
  if (error) return <Typography color="error">Error: {error}</Typography>;
  if (spans.length === 0) return <Typography>No spans found</Typography>;

  const traceDuration = traceInfo.duration || 1;

  return (
    <Box>
      <Paper className={classes.traceDetailHeader} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {spans[0]?.serviceName}: {spans[0]?.span.name}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2" color="textSecondary">
              <code>{traceID.substring(0, 16)}</code>
            </Typography>
          </Grid>
          <Grid item>
            <Chip label={`Duration ${formatDuration(traceInfo.duration)}`} size="small" />
          </Grid>
          <Grid item>
            <Chip label={`Services ${traceInfo.services}`} size="small" variant="outlined" />
          </Grid>
          <Grid item>
            <Chip label={`Depth ${traceInfo.maxDepth}`} size="small" variant="outlined" />
          </Grid>
          <Grid item>
            <Chip label={`Spans ${traceInfo.totalSpans}`} size="small" variant="outlined" />
          </Grid>
        </Grid>
      </Paper>

      {/* Timeline header */}
      <Box display="flex" style={{ padding: '4px 8px', borderBottom: '2px solid #ccc', fontSize: '0.75rem', color: '#888' }}>
        <Box style={{ width: 320, flexShrink: 0 }}>Service & Operation</Box>
        <Box flex={1} display="flex" justifyContent="space-between">
          <span>0ms</span>
          <span>{formatDuration(traceDuration * 0.25)}</span>
          <span>{formatDuration(traceDuration * 0.5)}</span>
          <span>{formatDuration(traceDuration * 0.75)}</span>
          <span>{formatDuration(traceDuration)}</span>
        </Box>
      </Box>

      {/* Span rows */}
      {spans.map(node => {
        const isError = node.span.status?.code === 2;
        const barLeft = (node.startOffsetMs / traceDuration) * 100;
        const barWidth = Math.max((node.durationMs / traceDuration) * 100, 0.5);
        const svcColor = getServiceColor(node.serviceName, colorMap.current);

        return (
          <div key={node.span.spanId} className={classes.spanRow}>
            <div
              className={classes.spanLabel}
              style={{ width: 320, paddingLeft: node.depth * 16 }}
            >
              {isError && <ErrorOutlineIcon style={{ fontSize: 14, color: '#d32f2f', verticalAlign: 'middle', marginRight: 2 }} />}
              <span style={{ color: svcColor, fontWeight: 500 }}>{node.serviceName}</span>
              {' '}
              <span style={{ color: '#666' }}>{node.span.name}</span>
            </div>
            <div className={classes.spanBarContainer}>
              <Tooltip title={`${node.serviceName}: ${node.span.name} — ${formatDuration(node.durationMs)}`}>
                <div
                  className={classes.spanBar}
                  style={{
                    backgroundColor: isError ? '#d32f2f' : svcColor,
                    marginLeft: `${barLeft}%`,
                    width: `${barWidth}%`,
                  }}
                />
              </Tooltip>
              <span style={{ position: 'absolute', left: `${barLeft + barWidth + 0.5}%`, fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap' }}>
                {formatDuration(node.durationMs)}
              </span>
            </div>
          </div>
        );
      })}
    </Box>
  );
};

/* ─── Scatter Tooltip ─── */

const ScatterTooltipContent: React.FC<{ active?: boolean; payload?: Array<{ payload: { service: string; operation: string; durationMs: number; time: string } }> }> = ({
  active,
  payload,
}) => {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <Paper style={{ padding: 8, fontSize: '0.8rem' }}>
      <div><strong>{d.service}</strong>: {d.operation}</div>
      <div>Duration: {formatDuration(d.durationMs)}</div>
      <div>Time: {d.time}</div>
    </Paper>
  );
};

/* ─── Main Component ─── */

export const TracesContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const ns =
    entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] ?? '';
  const entityName = entity?.metadata?.name ?? '';
  // OTel service.name override — entity name ≠ Tempo service.name 케이스
  // (예: arcus-dev → arcus-portal-dev). 표준: ops/docs/design/14-catalog-tab-standard.md §5
  const traceServiceName =
    entity?.metadata?.annotations?.['arcus.dev/trace-service-name'] ?? entityName;
  const entityKind = entity?.kind ?? '';
  const entityType = (entity?.spec as { type?: string })?.type ?? '';
  const isSystem = entityKind === 'System';
  const isFrontend = entityType === 'website';

  // For FE (website) entities, derive the backend service name for trace filtering.
  // Convention: FE entity name contains -fe- or ends with -web → replace to get BE name.
  // e.g., t01-fe-01 → t01-be-01, sales-crm-web → sales-crm-api
  const derivedServiceName = React.useMemo(() => {
    if (!isFrontend || !entityName) return '';
    if (entityName.includes('-fe-')) {
      return entityName.replace('-fe-', '-be-');
    }
    if (entityName.endsWith('-web')) {
      return entityName.replace(/-web$/, '-api');
    }
    return '';
  }, [isFrontend, entityName]);

  /* ─── State ─── */
  const [traces, setTraces] = React.useState<TraceResult[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [lookback, setLookback] = React.useState('1h');
  const [limit, setLimit] = React.useState(20);
  const [operationFilter, setOperationFilter] = React.useState('');
  const [minDuration, setMinDuration] = React.useState('');
  const [maxDuration, setMaxDuration] = React.useState('');
  const [errorOnly, setErrorOnly] = React.useState(false);
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const [serviceFilter, setServiceFilter] = React.useState<string>('ALL');
  const serviceFilterInitialized = React.useRef(false);

  /* ─── Derived ─── */
  const uniqueOperations = React.useMemo(
    () => [...new Set(traces.map(t => t.rootTraceName))].sort(),
    [traces],
  );

  const uniqueServices = React.useMemo(
    () => [...new Set(traces.map(t => t.rootServiceName).filter(Boolean))].sort(),
    [traces],
  );

  // Auto-select backend service for FE entities on initial load
  React.useEffect(() => {
    if (!serviceFilterInitialized.current && isFrontend && derivedServiceName) {
      setServiceFilter(derivedServiceName);
      serviceFilterInitialized.current = true;
    } else if (!serviceFilterInitialized.current && !isFrontend && traceServiceName && !isSystem) {
      setServiceFilter(traceServiceName);
      serviceFilterInitialized.current = true;
    }
  }, [isFrontend, derivedServiceName, traceServiceName, isSystem]);

  // Compute the effective service name for the query based on filter selection
  const effectiveServiceName = React.useMemo(() => {
    if (serviceFilter === 'ALL') return '';
    return serviceFilter;
  }, [serviceFilter]);

  /* ─── Build TraceQL ─── */
  const buildQuery = React.useCallback(() => {
    const conditions: string[] = [];
    if (ns) {
      if (isSystem && !ns.endsWith('-dev')) {
        conditions.push(`resource.service.namespace=~"${ns}(-dev)?"`);
      } else {
        conditions.push(`resource.service.namespace="${ns}"`);
      }
    }
    if (effectiveServiceName) {
      conditions.push(`resource.service.name="${effectiveServiceName}"`);
    }
    if (errorOnly) {
      conditions.push('status=error');
    }
    return conditions.length > 0 ? `{${conditions.join(' && ')}}` : '{}';
  }, [ns, isSystem, effectiveServiceName, errorOnly]);

  /* ─── Authenticated fetch (bypasses Backstage login popup) ─── */
  const authFetch = React.useCallback(async (url: string) => {
    const { token } = await identityApi.getCredentials();
    if (!token) {
      setSessionExpired(true);
      return undefined;
    }
    setSessionExpired(false);
    const resp = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (resp.status === 401) {
      setSessionExpired(true);
      return undefined;
    }
    return resp;
  }, [identityApi]);

  /* ─── Fetch ─── */
  const fetchTraces = React.useCallback(async () => {
    if (!ns) return;
    setLoading(true);
    setError(null);
    try {
      const now = Math.floor(Date.now() / 1000);
      const start = now - parseLookback(lookback);
      const q = buildQuery();

      const params = new URLSearchParams({
        q,
        start: String(start),
        end: String(now),
        limit: String(limit),
      });
      if (minDuration) params.set('minDuration', minDuration);
      if (maxDuration) params.set('maxDuration', maxDuration);

      const url = `/api/proxy/tempo/api/search?${params.toString()}`;
      const resp = await authFetch(url);
      if (!resp) return;
      if (!resp.ok) {
        throw new Error(`Tempo API error: ${resp.status} ${resp.statusText}`);
      }
      const data = await resp.json();
      let results: TraceResult[] = data.traces || [];

      // Client-side operation filter (Tempo search doesn't support rootTraceName filter)
      if (operationFilter) {
        results = results.filter(t => t.rootTraceName === operationFilter);
      }

      setTraces(results);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [ns, lookback, limit, operationFilter, minDuration, maxDuration, errorOnly, serviceFilter, buildQuery, authFetch]);

  React.useEffect(() => {
    fetchTraces();
  }, [fetchTraces]);

  if (!ns) {
    return (
      <EmptyState
        title="No namespace configured"
        missing="info"
        description="backstage.io/kubernetes-namespace annotation is required to display traces."
      />
    );
  }

  if (sessionExpired) {
    return (
      <EmptyState
        title="세션이 만료되었습니다"
        missing="info"
        description="페이지를 새로고침하면 정상 동작합니다."
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            새로고침
          </Button>
        }
      />
    );
  }

  /* ─── Scatter data ─── */
  const colorMap = React.useRef(new Map<string, string>());
  const scatterData = traces.map(t => {
    const timeMs = parseInt(t.startTimeUnixNano, 10) / 1e6;
    return {
      x: timeMs,
      y: t.durationMs,
      durationMs: t.durationMs,
      service: t.rootServiceName,
      operation: t.rootTraceName,
      traceID: t.traceID,
      time: formatTime(t.startTimeUnixNano),
      color: getServiceColor(t.rootServiceName, colorMap.current),
    };
  });

  /* ─── Table columns ─── */
  const columns: TableColumn<TraceResult>[] = [
    {
      title: 'Service / Operation',
      render: (row: TraceResult) => (
        <Box>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {row.rootServiceName}: {row.rootTraceName}
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ fontFamily: 'monospace' }}>
            {row.traceID.substring(0, 16)}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Duration',
      field: 'durationMs',
      render: (row: TraceResult) => (
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {formatDuration(row.durationMs)}
        </Typography>
      ),
      defaultSort: 'desc',
    },
    {
      title: 'Status',
      render: (row: TraceResult) => {
        const isErr = (row.rootTraceName || '').toLowerCase().includes('error');
        return isErr ? <StatusError>Error</StatusError> : <StatusOK>OK</StatusOK>;
      },
    },
    {
      title: 'Time',
      field: 'startTimeUnixNano',
      render: (row: TraceResult) => (
        <Typography variant="caption">{formatTime(row.startTimeUnixNano)}</Typography>
      ),
      defaultSort: 'desc',
    },
  ];

  return (
    <Grid container spacing={2} direction="column">
      {/* ─── Filter Bar ─── */}
      <Grid item>
        <Paper className={classes.filterBar} variant="outlined">
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <TextField
                select
                label="Service"
                value={serviceFilter}
                onChange={e => setServiceFilter(e.target.value)}
                variant="outlined"
                size="small"
                style={{ minWidth: 180 }}
              >
                <MenuItem value="ALL">All Services</MenuItem>
                {/* For non-System entities, show entity's own service name (or derived BE for FE) */}
                {!isSystem && traceServiceName && (
                  <MenuItem value={traceServiceName}>
                    {traceServiceName} (this)
                  </MenuItem>
                )}
                {!isSystem && isFrontend && derivedServiceName && (
                  <MenuItem value={derivedServiceName}>
                    {derivedServiceName} (backend)
                  </MenuItem>
                )}
                {/* Show discovered services from results, excluding already-listed ones */}
                {uniqueServices
                  .filter(s => s !== traceServiceName && s !== derivedServiceName)
                  .map(s => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item>
              <TextField
                select
                label="Operation"
                value={operationFilter}
                onChange={e => setOperationFilter(e.target.value)}
                variant="outlined"
                size="small"
                style={{ minWidth: 200 }}
              >
                <MenuItem value="">All Operations</MenuItem>
                {uniqueOperations.map(o => (
                  <MenuItem key={o} value={o}>{o}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item>
              <TextField
                select
                label="Lookback"
                value={lookback}
                onChange={e => setLookback(e.target.value)}
                variant="outlined"
                size="small"
                style={{ minWidth: 130 }}
              >
                {LOOKBACK_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item>
              <TextField
                label="Min Duration"
                value={minDuration}
                onChange={e => setMinDuration(e.target.value)}
                variant="outlined"
                size="small"
                style={{ width: 120 }}
                placeholder="e.g. 100ms"
                helperText=" "
              />
            </Grid>
            <Grid item>
              <TextField
                label="Max Duration"
                value={maxDuration}
                onChange={e => setMaxDuration(e.target.value)}
                variant="outlined"
                size="small"
                style={{ width: 120 }}
                placeholder="e.g. 5s"
                helperText=" "
              />
            </Grid>
            <Grid item>
              <TextField
                select
                label="Limit"
                value={limit}
                onChange={e => setLimit(Number(e.target.value))}
                variant="outlined"
                size="small"
                style={{ width: 80 }}
              >
                {LIMIT_OPTIONS.map(l => (
                  <MenuItem key={l} value={l}>{l}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item>
              <FormControlLabel
                control={
                  <Switch
                    checked={errorOnly}
                    onChange={e => setErrorOnly(e.target.checked)}
                    color="secondary"
                    size="small"
                  />
                }
                label="Errors only"
              />
            </Grid>
            <Grid item>
              <Button variant="contained" color="primary" size="small" onClick={fetchTraces}>
                Find Traces
              </Button>
            </Grid>
            <Grid item style={{ marginLeft: 'auto' }}>
              <Typography variant="caption" color="textSecondary">
                namespace: <strong>{ns}</strong>
                {isSystem && !ns.endsWith('-dev') && ' + dev'}
                {effectiveServiceName && (
                  <> | service: <strong>{effectiveServiceName}</strong></>
                )}
                {isFrontend && serviceFilter === 'ALL' && (
                  <> | <em>FE: use Service filter to show backend traces</em></>
                )}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* ─── Duration Scatter Plot ─── */}
      {traces.length > 0 && (
        <Grid item className={classes.scatterContainer}>
          <ResponsiveContainer width="100%" height={160}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="x"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={v => {
                  const d = new Date(v);
                  return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
                }}
                name="Time"
                fontSize={11}
              />
              <YAxis
                dataKey="y"
                type="number"
                tickFormatter={v => formatDuration(v)}
                name="Duration"
                fontSize={11}
                width={60}
              />
              <RechartsTooltip content={<ScatterTooltipContent />} />
              <Scatter data={scatterData}>
                {scatterData.map((entry, idx) => (
                  <Cell key={idx} fill={entry.color} fillOpacity={0.7} r={Math.max(4, Math.min(12, entry.durationMs / 50))} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </Grid>
      )}

      {/* ─── Traces Table ─── */}
      <Grid item>
        {error ? (
          <EmptyState title="Failed to load traces" missing="info" description={error} />
        ) : (
          <Table
            title={`${traces.length} Traces`}
            columns={columns}
            data={traces}
            isLoading={loading}
            options={{
              paging: true,
              pageSize: 20,
              sorting: true,
              search: true,
              padding: 'dense',
            }}
            detailPanel={({ rowData }) => (
              <Box style={{ padding: 8 }}>
                <TraceDetail
                  traceID={rowData.traceID}
                  fetchApi={fetchApi}
                  authFetch={authFetch}
                />
              </Box>
            )}
            emptyContent={
              <Typography variant="body1" style={{ padding: 32, textAlign: 'center' }}>
                No traces found for namespace <strong>{ns}</strong> in the last {lookback}.
                Ensure services have OTel instrumentation enabled.
              </Typography>
            }
          />
        )}
      </Grid>
    </Grid>
  );
};
